import React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RootStateType } from "@Store";
import { useDispatch, useSelector } from "react-redux";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { Button } from "@mui/material";
// Dialogs
import SharedLinkDialog from "../SharedLinkDialog";
import DeleteChannelDialog from "../DeleteChannelDialog";
import ChannelSettingsDialog from "../ChannelSettingsDialog";
import CloneChannelDialog from "../ChannelCloneDialog";
import UploadChannelCoverDialog from "../UploadChannelCoverDialog";
import VotedChoiceDialog from "../VotedChoiceDialog";
import ExportToDialog from "../ExportToDialog/ExportToDialog";
import {
    CHANNEL_COVER_ASPECT_RATIO,
    CHANNEL_COVER_BACKGROUND_STYLES,
} from "@Utils/channelCover";
import PollCreatorDialog from "../PollCreatorDialog";
import InvitationDialog from "../InvitationDialog";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import VoteConfirmationDialog from "../VoteConfirmationDialog";
import DeletePollDialog from "../DeletePollDialog";
import DataCollectionDialog from "../DataCollectionDialog";
import CreateChannelDialog from "../CreateChannelDialog/CreateChannelDialog";
import SubChannelSelectionDialog from "../SubChannelSelectionDialog";
import DeleteTextFieldDialog from "../DeleteTextFieldDialog";
import ComponentReorderModal from "../../ComponentReorderModal";
import CloseEventConfirmationDialog from "../CloseEventConfirmationDialog";
import AiReportContextDialog from "../AiReportContextDialog";
import InsufficientReportDataDialog from "../InsufficientReportDataDialog";
import PresentModeDialog from "../PresentModeDialog";
import CompletionRedirectDialog from "../CompletionRedirectDialog";
import { useTranslation } from "react-i18next";
import AssistantChartPreviewDialog from "../AssistantChartPreviewDialog";
import AssistantConversationExportErrorDialog from "../AssistantConversationExportErrorDialog";

type maxWidthType = "xs" | "sm" | "md" | "lg" | "xl" | false | undefined;

export const GlobalDialog: React.FC<any> = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const theme = useTheme();
    const responsiveFullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const open = useSelector((state: RootStateType) => state.dialog.open);
    const componentId = useSelector(
        (state: RootStateType) => state.dialog.componentId
    );

    // get from store the custom props
    const props = useSelector((state: RootStateType) => state.dialog.props);

    // Register the dialog
    const dialogComponents = {
        ChannelSettingsDialog,
        DeleteChannelDialog,
        SharedLinkDialog,
        UploadChannelCoverDialog,
        CloneChannelDialog,
        VotedChoiceDialog,
        ExportToDialog,
        InvitationDialog,
        PollCreatorDialog,
        VoteConfirmationDialog,
        DeletePollDialog,
        DeleteTextFieldDialog,
        DataCollectionDialog,
        CreateChannelDialog,
        SubChannelSelectionDialog,
        ComponentReorderModal,
        AssistantChartPreviewDialog,
        AssistantConversationExportErrorDialog,
        CloseEventConfirmationDialog,
        AiReportContextDialog,
        InsufficientReportDataDialog,
        PresentModeDialog,
        CompletionRedirectDialog,
    };

    // get from store the custom props
    const dialogComponent = (dialogComponents as any)[componentId as string];
    const {
        disableCancelButton = false,
        showActons = true,
        showHeader = false,
        isGuestData: backDropClick = true,
        showCloseButton = showHeader && backDropClick,
    } = dialogComponent ?? {};
    // Register the dialog

    const handleDialogClose = () => {
        dispatch(dialogActions.hideDialog());
    };

    const getCloseButton = () => (
        <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
                position: "absolute",
                right: 1,
                top: 1,
                color: "grey.500",
                zIndex: 1,
            }}
            size="large"
        >
            <CloseIcon />
        </IconButton>
    );

    //Default Dialog Header
    const getHeaderDialog = () => {
        const { header: Header } = (dialogComponents as any)[
            componentId as string
        ];
        return (
            <>
                <Header {...props} />
                {showCloseButton && getCloseButton()}
            </>
        );
    };

    //Default Dialog Content
    const getContentDialog = () => {
        const { content: Content } = (dialogComponents as any)[
            componentId as string
        ];
        return <Content {...props} />;
    };

    //Default Dialog Actions
    const getActionsDialog = () => {
        const { actions: Actions } = (dialogComponents as any)[
            componentId as string
        ];
        const actions = Actions ? <Actions {...props} /> : <></>;
        return (
            <>
                {disableCancelButton ? (
                    <></>
                ) : (
                    <Button
                        size="small"
                        color="secondary"
                        onClick={handleDialogClose}
                    >
                        {t("dialogs.cancel")}
                    </Button>
                )}
                {actions}
            </>
        );
    };

    return (
        <>
            {componentId && (
                <>
                    {componentId === "ComponentReorderModal" ? (
                        <ComponentReorderModal
                            channelId={(props as any)?.channelId}
                        />
                    ) : (
                        <Dialog
                            disableAutoFocus={true}
                            disableEnforceFocus={true}
                            fullWidth
                            open={open}
                            maxWidth={
                                props?.paperWidth
                                    ? false
                                    : (props?.maxWidth as maxWidthType)
                            }
                            aria-labelledby="form-dialog-title"
                            fullScreen={
                                props?.fullScreen ?? responsiveFullScreen
                            }
                            disableEscapeKeyDown={
                                props?.disableEscapeKeyDown ?? false
                            }
                            BackdropProps={{
                                sx: !backDropClick
                                    ? {
                                          backgroundColor: "#000000f0",
                                      }
                                    : undefined,
                            }}
                            onClose={(e, reason) => {
                                if (
                                    reason === "backdropClick" &&
                                    backDropClick
                                ) {
                                    handleDialogClose();
                                }
                            }}
                            PaperProps={{
                                sx: {
                                    position: "relative",
                                    ...(props?.paperWidth
                                        ? {
                                              width: props.paperWidth,
                                              maxWidth: "calc(100% - 64px)",
                                          }
                                        : {}),
                                },
                            }}
                        >
                            {!showHeader && showCloseButton && getCloseButton()}
                            {showHeader && (
                                <DialogTitle
                                    id="form-dialog-title"
                                    sx={{
                                        backgroundColor: "#2E4E76",
                                        padding: "35px 24px",
                                        ...(props?.coverUrl
                                            ? {
                                                  backgroundImage: `url(${props.coverUrl})`,
                                                  ...CHANNEL_COVER_BACKGROUND_STYLES,
                                                  aspectRatio: `${CHANNEL_COVER_ASPECT_RATIO}`,
                                                  boxSizing: "border-box",
                                              }
                                            : {}),
                                    }}
                                    children={getHeaderDialog()}
                                ></DialogTitle>
                            )}
                            <DialogContent
                                children={getContentDialog()}
                            ></DialogContent>
                            {showActons && (
                                <DialogActions
                                    children={getActionsDialog()}
                                ></DialogActions>
                            )}
                        </Dialog>
                    )}
                </>
            )}
        </>
    );
};
